import { useMemo, useState, type FormEvent } from 'react';
import { CheckCircle2, Component, Info as InfoIcon, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { ErrorModal, InfoModal, LogModal, WarningModal } from '../../components/AppStatusModal';

const componentInventory = [
  'accordion',
  'alert-dialog',
  'alert',
  'aspect-ratio',
  'avatar',
  'badge',
  'breadcrumb',
  'button-group',
  'button',
  'calendar',
  'card',
  'carousel',
  'checkbox',
  'collapsible',
  'command',
  'context-menu',
  'dialog',
  'drawer',
  'dropdown-menu',
  'empty',
  'field',
  'form',
  'hover-card',
  'input-group',
  'input-otp',
  'input',
  'item',
  'kbd',
  'label',
  'menubar',
  'navigation-menu',
  'pagination',
  'popover',
  'progress',
  'radio-group',
  'scroll-area',
  'select',
  'separator',
  'sheet',
  'sidebar',
  'skeleton',
  'slider',
  'sonner',
  'spinner',
  'switch',
  'table',
  'tabs',
  'textarea',
  'toast',
  'toaster',
  'toggle-group',
  'toggle',
  'tooltip',
  'use-mobile',
  'use-toast',
] as const;

export default function UIComponents() {
  const [switchEnabled, setSwitchEnabled] = useState(true);
  const [requiredName, setRequiredName] = useState('');
  const [requiredEmail, setRequiredEmail] = useState('');
  const [requiredDescription, setRequiredDescription] = useState('');
  const [progressValue, setProgressValue] = useState(72);
  const [sliderValue, setSliderValue] = useState<number[]>([35]);
  const [radioValue, setRadioValue] = useState('option-a');
  const [isChecked, setIsChecked] = useState(true);

  const [logModalOpen, setLogModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);

  const totalComponents = useMemo(() => componentInventory.length, []);

  const handleRequiredFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    toast.success('Required form submitted', {
      description: `Name: ${requiredName} | Email: ${requiredEmail}`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border-subtle bg-card p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-primary">
              Design System
            </p>
            <h1 className="mt-1 text-3xl font-bold text-text-main">UI Components</h1>
            <p className="mt-2 text-sm text-text-subtle">
              Centralized showcase of common components and modal variants.
            </p>
          </div>
          <Badge variant="secondary" className="text-xs">
            {totalComponents} Common Components
          </Badge>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Common Components</CardTitle>
          <CardDescription>Full inventory under src/components/ui.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {componentInventory.map((name) => (
              <Badge key={name} variant="outline" className="justify-start">
                {name}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Core Inputs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form className="space-y-3" onSubmit={handleRequiredFormSubmit}>
              <Input
                required
                value={requiredName}
                onChange={(e) => setRequiredName(e.target.value)}
                placeholder="Full name (required)"
              />
              <Input
                required
                type="email"
                value={requiredEmail}
                onChange={(e) => setRequiredEmail(e.target.value)}
                placeholder="Work email (required)"
              />
              <Textarea
                required
                rows={3}
                value={requiredDescription}
                onChange={(e) => setRequiredDescription(e.target.value)}
                placeholder="Short description (required)"
              />
              <div className="flex flex-wrap gap-2">
                <Button type="submit">Submit Required Form</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    toast.info('Info notification', {
                      description: 'This is a sonner info toast.',
                    })
                  }
                >
                  Show Info Toast
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    toast.warning('Warning notification', {
                      description: 'Please review the required fields.',
                    })
                  }
                >
                  Show Warning Toast
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() =>
                    toast.error('Error notification', {
                      description: 'Simulated error from UI Components page.',
                    })
                  }
                >
                  Show Error Toast
                </Button>
              </div>
            </form>

            <Select defaultValue="junior">
              <SelectTrigger>
                <SelectValue placeholder="Select value" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="intern">Intern</SelectItem>
                <SelectItem value="junior">Junior</SelectItem>
                <SelectItem value="senior">Senior</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-3">
              <Checkbox
                checked={isChecked}
                onCheckedChange={(checked) => setIsChecked(checked === true)}
              />
              <span className="text-sm text-text-main">Checkbox component</span>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={switchEnabled} onCheckedChange={setSwitchEnabled} />
              <span className="text-sm text-text-main">Switch component</span>
            </div>
            <RadioGroup value={radioValue} onValueChange={setRadioValue} className="gap-2">
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="option-a" /> Option A
              </label>
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="option-b" /> Option B
              </label>
            </RadioGroup>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Feedback Components</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertTitle>Default Alert</AlertTitle>
              <AlertDescription>Use this block for contextual information.</AlertDescription>
            </Alert>
            <Alert variant="destructive">
              <TriangleAlert className="h-4 w-4" />
              <AlertTitle>Destructive Alert</AlertTitle>
              <AlertDescription>Use this block for critical error states.</AlertDescription>
            </Alert>
            <div>
              <div className="mb-2 text-sm font-medium text-text-main">
                Progress: {progressValue}%
              </div>
              <Progress value={progressValue} />
              <Slider
                className="mt-3"
                min={0}
                max={100}
                step={1}
                value={sliderValue}
                onValueChange={(values) => {
                  setSliderValue(values);
                  setProgressValue(values[0] ?? 0);
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Buttons, Tabs, Table</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button>Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
          </div>

          <Tabs defaultValue="table">
            <TabsList>
              <TabsTrigger value="table">Table</TabsTrigger>
              <TabsTrigger value="badges">Badges</TabsTrigger>
            </TabsList>
            <TabsContent value="table" className="pt-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Component</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Version</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell>Button</TableCell>
                    <TableCell>Ready</TableCell>
                    <TableCell>v1</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Dialog</TableCell>
                    <TableCell>Ready</TableCell>
                    <TableCell>v1</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TabsContent>
            <TabsContent value="badges" className="pt-3">
              <div className="flex flex-wrap gap-2">
                <Badge>Default</Badge>
                <Badge variant="secondary">Secondary</Badge>
                <Badge variant="outline">Outline</Badge>
                <Badge variant="destructive">Destructive</Badge>
              </div>
            </TabsContent>
          </Tabs>
          <Separator />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Modal Variants (Props-driven)</CardTitle>
          <CardDescription>
            Log, Info, Error, Warning modal components with configurable props.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setLogModalOpen(true)}>
              <Component size={14} /> Open Log Modal
            </Button>
            <Button variant="outline" onClick={() => setInfoModalOpen(true)}>
              <InfoIcon size={14} /> Open Info Modal
            </Button>
            <Button variant="outline" onClick={() => setErrorModalOpen(true)}>
              <TriangleAlert size={14} /> Open Error Modal
            </Button>
            <Button variant="outline" onClick={() => setWarningModalOpen(true)}>
              <CheckCircle2 size={14} /> Open Warning Modal
            </Button>
          </div>
        </CardContent>
      </Card>

      <LogModal
        open={logModalOpen}
        onOpenChange={setLogModalOpen}
        title="Activity Log"
        description="Recent system activities for this page."
        details={
          <ul className="list-disc pl-5">
            <li>UI catalog loaded successfully.</li>
            <li>54 common components indexed.</li>
            <li>Last sync: just now.</li>
          </ul>
        }
        confirmText="Close"
        showCancel={false}
        onConfirm={() =>
          toast.success('Log modal closed', {
            description: 'Activity log reviewed.',
          })
        }
      />

      <InfoModal
        open={infoModalOpen}
        onOpenChange={setInfoModalOpen}
        title="Information"
        description="This modal can receive title, description, details and action callbacks via props."
        details="Use this for non-critical guidance."
        confirmText="Understood"
        onConfirm={() =>
          toast.success('Info acknowledged', {
            description: 'User confirmed the information modal.',
          })
        }
      />

      <ErrorModal
        open={errorModalOpen}
        onOpenChange={setErrorModalOpen}
        title="Error"
        description="Unable to fetch component metadata from remote source."
        details="Error code: UI-COMP-500"
        confirmText="Retry"
        cancelText="Dismiss"
        onConfirm={() =>
          toast.warning('Retry action triggered', {
            description: 'Attempting to recover component metadata.',
          })
        }
        onCancel={() =>
          toast.info('Error modal dismissed', {
            description: 'No retry was performed.',
          })
        }
      />

      <WarningModal
        open={warningModalOpen}
        onOpenChange={setWarningModalOpen}
        title="Warning"
        description="You are about to replace an existing component variant."
        details="This action may affect pages using shared UI primitives."
        confirmText="Continue"
        cancelText="Cancel"
        onConfirm={() =>
          toast.success('Warning accepted', {
            description: 'Continuation action has been approved.',
          })
        }
        onCancel={() =>
          toast.info('Warning cancelled', {
            description: 'Operation was safely cancelled.',
          })
        }
      />
    </div>
  );
}
